import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Lock, Plus, Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ColumnMeta, FieldDef } from '@/lib/types';
import { Dialog } from '@/ui/Dialog';
import { Spinner } from '@/ui/Spinner';
import { EditGrid, EditRow, isDirty, toEditRows } from '@/grid/EditGrid';
import { dizaynColumns } from '../columns';
import { formatValue, parseNumberInput } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Row } from './types';

interface KalemTanim {
  grup: string; tur: 'servis' | 'irsaliye' | 'fatura'; kilitAlan: string | null; tahsilat: boolean;
  master: { levelId: number; fields: FieldDef[]; height: number | null };
  detail: { levelId: number; fields: FieldDef[] };
}

const up = (s: string) => s.toLocaleUpperCase('tr-TR');
const val = (r: Row | null | undefined, f: string) => (r ? r[Object.keys(r).find((k) => up(k) === up(f)) ?? f] : undefined);
const money = (v: unknown) => formatValue(Number(v ?? 0), 'float', '#,##0.00');

/**
 * Kalem girişi — Delphi Firin\Unit80_21 (Servis), 80_31 (İrsaliye), 80_41 (Fatura) Kalem Girişi.
 * Cari cari ilerlenir (F11 İlk · F8 Geri · F9 İleri · F12 Son); satır değişince kalem hemen kaydedilir ve
 * cari toplamları sunucuda Delphi kuralıyla güncellenir. Servis fişinde '+' tahsilatı bakiyeyle doldurur.
 */
export function KalemGirisi({ tag, belgeId, cariId, baslik, onClose }: { tag: number; belgeId: number; cariId: number | null; baslik: string; onClose: () => void }) {
  const tq = useQuery({ queryKey: ['kalem-tanim', tag], queryFn: () => api<KalemTanim>(`/screens/${tag}/kalem/tanim`) });
  const t = tq.data;
  const [masters, setMasters] = useState<Row[] | null>(null);
  const [masterCols, setMasterCols] = useState<ColumnMeta[]>([]);
  const [idx, setIdx] = useState(0);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [detailCols, setDetailCols] = useState<ColumnMeta[]>([]);
  const [sel, setSel] = useState<number | null>(0);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [tahsilat, setTahsilat] = useState('');
  const tahsilatRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  // Master_Open + FormCreate: Filitre (CARI_ID) satırına konumlan
  useEffect(() => {
    api<{ rows: Row[]; columns: ColumnMeta[] }>(`/screens/${tag}/kalem/yukle/${belgeId}`).then((r) => {
      setMasters(r.rows);
      setMasterCols(r.columns);
      const i = cariId ? r.rows.findIndex((x) => Number(val(x, 'CARI_ID')) === cariId) : 0;
      setIdx(i >= 0 ? i : 0);
    }).catch((e) => toast.error(e.message));
  }, [tag, belgeId, cariId]);

  const cur = masters?.[idx] ?? null;
  const curId = Number(val(cur, 'ID') ?? 0);
  const kilit = !!t?.kilitAlan && Number(val(cur, t.kilitAlan) ?? 0) > 0;

  const loadDetail = useCallback(async (masterRowId: number) => {
    const r = await api<{ rows: Row[]; columns: ColumnMeta[] }>(`/screens/${tag}/kalem/detay/${masterRowId}`);
    setRows(toEditRows(r.rows));
    setDetailCols(r.columns);
    setSel(r.rows.length ? 0 : null);
  }, [tag]);
  useEffect(() => { if (curId) loadDetail(curId).catch((e) => toast.error(e.message)); }, [curId, loadDetail]);
  useEffect(() => { setTahsilat(cur ? String(val(cur, 'TAHSILAT') ?? 0).replace('.', ',') : ''); }, [cur]);

  const replaceMaster = (m: Row | null) => {
    if (!m) return;
    setMasters((ms) => (ms ? ms.map((x) => (Number(val(x, 'ID')) === Number(val(m, 'ID')) ? m : x)) : ms));
  };

  /** MSQuery2.Post: kalem satırı + cari toplamı */
  const saveRow = useCallback(async (i: number, list = rows) => {
    const x = list[i];
    if (!x || !isDirty(x) || kilit) return true;
    try {
      const res = await api<{ row: Row; master: Row | null; uyari: string | null }>(`/screens/${tag}/kalem/satir`, { body: { masterRowId: curId, row: x.row } });
      setRows((rs) => rs.map((y) => (y._k === x._k ? { ...y, row: res.row, original: { ...res.row } } : y)));
      replaceMaster(res.master);
      if (res.uyari) toast.warning(res.uyari);
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  }, [rows, tag, curId, kilit]);

  const flush = useCallback(async () => {
    for (let i = 0; i < rows.length; i++) if (isDirty(rows[i]) && !(await saveRow(i))) return false;
    return true;
  }, [rows, saveRow]);

  const go = async (n: number) => {
    if (!masters?.length) return;
    if (!(await flush())) return;
    setIdx(Math.max(0, Math.min(masters.length - 1, n)));
  };

  const saveMaster = async (patch: Row) => {
    if (!cur || kilit) return;
    setBusy(true);
    try {
      const res = await api<{ master: Row | null }>(`/screens/${tag}/kalem/master`, { body: { row: { ID: curId, ...patch } } });
      replaceMaster(res.master);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const tahsilatKaydet = async () => {
    const n = parseNumberInput(tahsilat);
    if (tahsilat.trim() && n == null) { toast.error('Geçerli bir sayı giriniz'); return false; }
    if (String(n ?? 0) !== String(Number(val(cur, 'TAHSILAT') ?? 0))) await saveMaster({ TAHSILAT: n ?? 0 });
    return true;
  };
  // '+' (VK_ADD): TAHSILAT := TOPLAM_TUTAR - IADE_TUTAR
  const tahsilatDoldur = async () => {
    if (!t?.tahsilat || !cur || kilit) return;
    if (!(await flush())) return;
    try {
      const res = await api<{ master: Row | null }>(`/screens/${tag}/kalem/tahsilat`, { body: { masterRowId: curId } });
      replaceMaster(res.master);
      nextRef.current?.focus();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const close = async () => {
    await flush();
    if (t?.tahsilat) await tahsilatKaydet();
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'F11') { e.preventDefault(); go(0); }
    else if (k === 'F8') { e.preventDefault(); go(idx - 1); }
    else if (k === 'F9') { e.preventDefault(); go(idx + 1); }
    else if (k === 'F12') { e.preventDefault(); go((masters?.length ?? 1) - 1); }
    else if (k === 'F5') { e.preventDefault(); flush().then((ok) => ok && toast.success('Kaydedildi')); }
    else if ((k === '+' || e.code === 'NumpadAdd') && t?.tahsilat && (e.target as HTMLElement).tagName !== 'INPUT') { e.preventDefault(); tahsilatDoldur(); }
  };

  const columns = useMemo(() => (t ? dizaynColumns(detailCols, t.detail.fields, { tag, editableCols: kilit ? new Set() : undefined }) : []), [t, detailCols, tag, kilit]);
  const visibleMasterFields = useMemo(() => (t ? t.master.fields.filter((f) => !f.hidden && masterCols.some((c) => up(c.name) === up(f.field))) : []), [t, masterCols]);
  const filtered = useMemo(() => {
    if (!masters) return [];
    const q = filter.trim().toLocaleLowerCase('tr-TR');
    return masters.map((m, i) => ({ m, i })).filter(({ m }) => !q || `${val(m, 'CARI_KODU') ?? ''} ${val(m, 'CARI_UNVAN') ?? ''} ${val(m, 'EVRAK_NO') ?? ''}`.toLocaleLowerCase('tr-TR').includes(q));
  }, [masters, filter]);

  const tutar = Number(val(cur, 'TOPLAM_TUTAR') ?? 0), iadeT = Number(val(cur, 'IADE_TUTAR') ?? 0);

  return (
    <Dialog open onOpenChange={(o) => !o && close()} title={baslik} className="h-[92vh] w-[min(1280px,97vw)]">
      {!t || !masters ? <Spinner /> : (
        <div className="flex h-full min-h-0 gap-3" onKeyDown={onKeyDown}>
          {/* cari listesi */}
          <div className="hidden w-64 shrink-0 flex-col gap-2 md:flex">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 muted" />
              <input className="inp w-full !pl-7" placeholder="Cari ara…" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            <div className="min-h-0 flex-1 overflow-auto rounded border b-border scroll-thin">
              {filtered.map(({ m, i }) => {
                const toplam = Number(val(m, 'TOPLAM_TUTAR') ?? 0), tah = Number(val(m, 'TAHSILAT') ?? 0);
                return (
                  <button key={String(val(m, 'ID'))} onClick={() => go(i)}
                    className={cn('flex w-full flex-col items-start gap-0.5 border-b b-border px-2.5 py-1.5 text-left', i === idx ? 'bg-[var(--grid-select)]' : 'hover:bg-[var(--grid-hover)]')}>
                    <span className="flex w-full items-center gap-1.5 text-[12px] font-semibold">
                      <span className="num muted">{val(m, 'CARI_KODU')}</span>
                      {Number(val(m, 'TOPLAM_ADET') ?? 0) > 0 && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[var(--success)]" />}
                    </span>
                    <span className="w-full truncate text-[12px]">{val(m, 'CARI_UNVAN')}</span>
                    {toplam > 0 && <span className="num text-[11px] muted">{money(toplam)} · tahsilat {money(tah)}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* seçili cari */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <button className="btn" onClick={() => go(0)} title="İlk (F11)"><ChevronFirst size={14} /></button>
              <button className="btn" onClick={() => go(idx - 1)} title="Geri (F8)"><ChevronLeft size={14} /> Geri</button>
              <span className="num px-2 text-[12px] muted">{masters.length ? idx + 1 : 0} / {masters.length}</span>
              <button ref={nextRef} className="btn btn-primary" onClick={() => go(idx + 1)} title="İleri (F9)">İleri <ChevronRight size={14} /></button>
              <button className="btn" onClick={() => go(masters.length - 1)} title="Son (F12)"><ChevronLast size={14} /></button>
              <button className="btn ml-2" onClick={() => flush().then((ok) => ok && toast.success('Kaydedildi'))} title="Kaydet (F5)"><Save size={14} /> Kaydet</button>
              {kilit && <span className="badge ml-2 bg-[var(--danger-soft)] text-[var(--danger-text)]"><Lock size={12} /> {t.tur === 'fatura' ? 'İrsaliyeden aktarılmış — düzeltilemez' : 'Faturası kesilmiş — düzeltilemez'}</span>}
              {busy && <span className="text-[11px] muted">kaydediliyor…</span>}
            </div>

            {cur ? (
              <div className="card grid gap-3 p-3 lg:grid-cols-[1fr_auto]">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="num text-[12px] muted">{val(cur, 'CARI_KODU')}</span>
                    {val(cur, 'TIPI') && <span className="badge chip-accent">{val(cur, 'TIPI')}</span>}
                  </div>
                  <div className="h-display truncate text-[18px]">{val(cur, 'CARI_UNVAN')}</div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
                    {visibleMasterFields.filter((f) => !['CARI_KODU', 'CARI_UNVAN', 'TIPI', 'TAHSILAT'].includes(up(f.field))).map((f) => (
                      <MasterField key={f.id} f={f} row={cur} disabled={kilit} onSave={(v) => saveMaster({ [f.field]: v })} />
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <Stat label="Adet" value={formatValue(Number(val(cur, 'TOPLAM_ADET') ?? 0), 'float', '#,##0.##')} />
                  {t.tur === 'servis' && <Stat label="İade" value={formatValue(Number(val(cur, 'TOPLAM_IADE') ?? 0), 'float', '#,##0.##')} />}
                  <Stat label="Tutar" value={money(tutar)} strong />
                  {t.tur === 'servis' ? <Stat label="İade tutarı" value={money(iadeT)} /> : <Stat label="Genel toplam" value={money(val(cur, 'GENEL_TOPLAM'))} strong />}
                  {t.tahsilat && (
                    <label className="flex flex-col gap-0.5">
                      <span className="h-label">Tahsilat Tutarı</span>
                      <div className="flex gap-1">
                        <input ref={tahsilatRef} className="inp w-32 text-right num !text-[15px] font-semibold" value={tahsilat} disabled={kilit}
                          onChange={(e) => setTahsilat(e.target.value)} onBlur={tahsilatKaydet}
                          onKeyDown={async (e) => { if (e.key === 'Enter') { e.preventDefault(); if (await tahsilatKaydet()) go(idx + 1); } if (e.key === '+') { e.preventDefault(); tahsilatDoldur(); } }} />
                        <button className="btn" disabled={kilit} onClick={tahsilatDoldur} title="Bakiyeyi tahsil et (+)"><Plus size={14} /></button>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            ) : <div className="card p-6 text-center muted">Bu belgede cari satırı yok.</div>}

            <EditGrid className="min-h-0 flex-1" columns={columns} rows={rows} selected={sel} onSelect={setSel}
              layoutKey={`kalem:${t.grup}`} readOnly={kilit}
              onChange={(i, p) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, row: { ...x.row, ...p } } : x)))}
              onRowLeave={(i) => { saveRow(i); }}
              emptyText="Bu cari için kalem yok (cari fiyat listesi tanımlı değil)" />
            <div className="text-[11px] muted">
              Miktar/İade girip satırdan çıkınca kaydedilir · <span className="kbd">F9</span> sonraki cari · <span className="kbd">F8</span> önceki
              {t.tahsilat && <> · <span className="kbd">+</span> tahsilatı bakiyeyle doldur · tahsilatta <span className="kbd">Enter</span> sonraki cari</>}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-[var(--r-input)] border b-border bg-[var(--fill)] px-2.5 py-1">
      <div className="h-label !text-[10px]">{label}</div>
      <div className={cn('num text-right', strong ? 'text-[15px] font-semibold' : 'text-[13px]')}>{value}</div>
    </div>
  );
}

function MasterField({ f, row, disabled, onSave }: { f: FieldDef; row: Row; disabled: boolean; onSave: (v: unknown) => void }) {
  const raw = val(row, f.field);
  const [v, setV] = useState(String(raw ?? ''));
  useEffect(() => setV(String(raw ?? '')), [raw]);
  const ro = disabled || f.readonly;
  const shown = typeof raw === 'number' ? formatValue(raw, 'float', f.mask) : String(raw ?? '');
  return (
    <span className="flex items-center gap-1.5">
      <span className="muted">{f.caption || f.field}:</span>
      {ro ? <b className="num">{shown || '—'}</b> : (
        <input className="inp h-7 w-28" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== String(raw ?? '') && onSave(v)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      )}
    </span>
  );
}
